import { useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client/react';

import { ISkillListItemFragment, SkillsDocument } from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { usePagination } from 'common/hooks/usePagination';
import { useSkillReportFilter } from 'skill/hooks/useSkillReportFilter';

export const useSkillList = (filterParamsInUrl = true) => {
    const { showApolloError } = useApolloError();

    const {
        filters,
        filterValues,
        filterValuesForQuery,
        handleSearch: onSearch,
        handleFilterChange: onFilterChange,
    } = useSkillReportFilter(filterParamsInUrl);

    const { paginationSettings, initializePagination, setPage } =
        usePagination(true);

    const paginationVariables = useMemo(
        () => ({
            offset: paginationSettings.offset,
            first: paginationSettings.first,
        }),
        [paginationSettings]
    );

    // Build variables for query
    const variables = useMemo(
        () => ({
            ...paginationVariables,
            ...filterValuesForQuery,
        }),
        [paginationVariables, filterValuesForQuery]
    );

    const {
        loading: skillsLoading,
        data: skillsData,
        error,
    } = useQuery(SkillsDocument, {
        variables,
    });

    useEffect(() => {
        if (error) {
            showApolloError(error);
        }
    }, [error, showApolloError]);

    const skillsObject = skillsData?.skills;
    const skillsCount = skillsObject?.count;
    const skills = useMemo(
        () =>
            (skillsObject?.edges
                .map((edge) => edge?.node || null)
                .filter(Boolean) as ISkillListItemFragment[]) || [],
        [skillsObject?.edges]
    );

    const loading = skillsLoading;

    useEffect(() => {
        if (loading || !initializePagination) return;

        initializePagination(skillsCount || 0);
    }, [skillsCount, loading, initializePagination]);

    return {
        skills,
        skillsCount,
        loading,
        paginationSettings,
        filters,
        filterValues,
        onSearch,
        onFilterChange,
        setPage,
    };
};
